import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

// Silent-refresh policy: while the tab is active, call /auth/refresh at most
// once every REFRESH_INTERVAL_MS to keep the 24h admin JWT alive. Any user
// activity (click, keydown, mousemove) marks the tab as active. Idle tabs
// stop refreshing so an unattended browser still expires on schedule.
const REFRESH_INTERVAL_MS = 15 * 60 * 1000;  // 15 minutes
const ACTIVITY_WINDOW_MS = 15 * 60 * 1000;   // 15 minutes of idle = stop refreshing

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastActivityRef = useRef(Date.now());

  // On mount, check if we have a valid session via httpOnly cookie
  useEffect(() => {
    const checkSession = async () => {
      try {
        const res = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
        if (res.data) setUser(res.data);
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkSession();
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/api/auth/login`, { email, password }, { withCredentials: true });
    // Fetch full user profile (includes tour_completed, language_preference, etc.)
    let resolved;
    try {
      const meRes = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      resolved = meRes.data;
    } catch {
      resolved = res.data.user;
    }
    setUser(resolved);
    return resolved;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    setUser(null);
  };

  const axiosAuth = useCallback(() => {
    return axios.create({
      baseURL: `${API}/api`,
      withCredentials: true,
    });
  }, []);

  // Silent JWT refresh: whenever the tab has seen recent user activity,
  // rotate the token every REFRESH_INTERVAL_MS. This keeps long working
  // sessions alive without ever forcing a mid-workday re-login.
  useEffect(() => {
    if (!user) return;
    const markActive = () => { lastActivityRef.current = Date.now(); };
    const events = ['mousemove', 'keydown', 'click', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, markActive, { passive: true }));

    const tick = async () => {
      const idleMs = Date.now() - lastActivityRef.current;
      if (idleMs > ACTIVITY_WINDOW_MS) return;  // user is away, let session lapse
      try {
        await axios.post(`${API}/api/auth/refresh`, {}, { withCredentials: true });
      } catch {
        // Refresh can fail (network blip, session already dead). We do not
        // eagerly log the user out — the next real API call will 401 and
        // route them to login through the existing flow.
      }
    };
    const id = window.setInterval(tick, REFRESH_INTERVAL_MS);
    return () => {
      window.clearInterval(id);
      events.forEach((e) => window.removeEventListener(e, markActive));
    };
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, axiosAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
